import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const CHECK_ITEMS = [
  { key: 'length',  label: '8+ characters' },
  { key: 'upper',   label: 'Uppercase letter' },
  { key: 'lower',   label: 'Lowercase letter' },
  { key: 'number',  label: 'Number' },
  { key: 'special', label: 'Special character' },
  { key: 'match',   label: 'Passwords match' },
];

const Signup = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: ''
  });
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [checks, setChecks] = useState({
    length: false, upper: false, lower: false, number: false, special: false, match: false
  });

  const [isSignedUp, setIsSignedUp] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const navigate = useNavigate();
  const { signup, loginWithGoogle, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;
    setChecks({
      length: password.length >= 8,
      upper:  /[A-Z]/.test(password),
      lower:  /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[@$!%*?&]/.test(password),
      match:  password === confirmPassword && password.length > 0,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.password, formData.confirmPassword]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === 'email' ? value.toLowerCase() : value;
    setFormData({ ...formData, [name]: finalValue });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email) {
      setStatus({ type: 'warning', text: 'First name, last name, and email are required.' });
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setStatus({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    if (!Object.values(checks).every(Boolean)) {
      setStatus({ type: 'warning', text: 'Please fulfill all password requirements.' });
      return;
    }

    setIsSubmitting(true);
    try {
      await signup({
        firstName: formData.firstName,
        lastName:  formData.lastName,
        email:     formData.email,
        password:  formData.password,
      });
      setRegisteredEmail(formData.email);
      setIsSignedUp(true);
      setStatus({ 
        type: 'success', 
        text: 'Account created! Please check your email to verify your account to continue.' 
      });
      setFormData({
        firstName: '', lastName: '', email: '', password: '', confirmPassword: ''
      });
    } catch (err) {
      if (err.response?.status === 422) {
        const detail = err.response.data.detail;
        const errorMsg = Array.isArray(detail) ? detail[0].msg : detail;
        setStatus({ type: 'error', text: errorMsg });
      } else {
        setStatus({ type: 'error', text: err.response?.data?.detail || 'Signup failed' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignup = () => {
    setStatus({ type: 'info', text: 'Redirecting to Google…' });
    loginWithGoogle();
  };

  const statusColors = {
    success: { color: 'var(--color-success)', bg: 'var(--color-success-bg)', border: 'var(--color-success-border)' },
    error:   { color: 'var(--color-error)',   bg: 'var(--color-error-bg)',   border: 'var(--color-error-border)'   },
    warning: { color: 'var(--color-warning)', bg: 'var(--color-warning-bg)', border: 'var(--color-warning-border)' },
    info:    { color: 'var(--color-text-secondary)', bg: 'var(--color-surface-alt)', border: 'var(--color-border)' },
  };

  const inputStyle = (field) => ({
    ...styles.input,
    ...(focusedField === field ? styles.inputFocused : {}),
  });

  if (isSignedUp) {
    return (
      <div style={styles.page}>
        <div className="auth-blob-decoration" style={styles.blobTopRight}  aria-hidden="true" />
        <div className="auth-blob-decoration" style={styles.blobBottomLeft} aria-hidden="true" />

        <main style={styles.card} className="glass-panel auth-card-container">
          <header style={styles.brandArea}>
            <span style={styles.logoMark} aria-hidden="true">⬡</span>
            <h1 style={styles.brandName}>RezFlow</h1>
          </header>
          
          <div style={styles.successContainer}>
            <div style={styles.badgeWrapper}>
              <div style={styles.glowCircle} />
              <div style={styles.mailIconCelebrating}>✉️</div>
              
              {/* Celebrate Confetti Burst */}
              <div className="confetti-container" style={{ position: 'absolute', pointerEvents: 'none' }}>
                <div className="confetti-piece p0" />
                <div className="confetti-piece p1" />
                <div className="confetti-piece p2" />
                <div className="confetti-piece p3" />
                <div className="confetti-piece p4" />
                <div className="confetti-piece p5" />
                <div className="confetti-piece p6" />
                <div className="confetti-piece p7" />
                <div className="confetti-piece p8" />
                <div className="confetti-piece p9" />
                <div className="confetti-piece p10" />
                <div className="confetti-piece p11" />
              </div>
            </div>
            
            <h2 style={styles.successHeading}>Almost there! 🚀</h2>
            <p style={styles.celebrationText}>
              Please check your inbox to activate your account.
            </p>
            
            <div style={styles.emailNotificationCard}>
              <p style={styles.successText}>
                We've sent a verification link to:<br/>
                <strong style={{ color: 'var(--color-charcoal-900)', wordBreak: 'break-all' }}>{registeredEmail}</strong>
              </p>
            </div>
            
            <p style={styles.subtext}>
              Click the link in your email to start your resume optimization journey!
            </p>
            
            <button
              onClick={() => navigate('/login')}
              style={styles.premiumRedirectBtn}
              className="hover-lift"
            >
              Back to Sign In
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div className="auth-blob-decoration" style={styles.blobTopRight}  aria-hidden="true" />
      <div className="auth-blob-decoration" style={styles.blobBottomLeft} aria-hidden="true" />

      <main style={styles.card} className="glass-panel auth-card-container">
        {/* Brand */}
        <header style={styles.brandArea}>
          <span style={styles.logoMark} aria-hidden="true">⬡</span>
          <h1 style={styles.brandName}>RezFlow</h1>
        </header>
        <p style={styles.tagline}>Create your free account</p>

        {/* Google SSO */}
        <button
          type="button"
          onClick={handleGoogleSignup}
          style={styles.googleBtn}
          disabled={isSubmitting}
          className="hover-lift"
          aria-label="Continue with Google"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={styles.divider} role="separator">
          <span style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <span style={styles.dividerLine} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          {/* Name row */}
          <div style={styles.nameRow}>
            <div style={styles.fieldWrap}>
              <label htmlFor="su-firstName" style={styles.label}>First Name</label>
              <input
                id="su-firstName"
                name="firstName"
                placeholder="Jane"
                onChange={handleChange}
                onFocus={() => setFocusedField('firstName')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('firstName')}
                disabled={isSubmitting}
                autoComplete="given-name"
                required
              />
            </div>
            <div style={styles.fieldWrap}>
              <label htmlFor="su-lastName" style={styles.label}>Last Name</label>
              <input
                id="su-lastName"
                name="lastName"
                placeholder="Doe"
                onChange={handleChange}
                onFocus={() => setFocusedField('lastName')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('lastName')}
                disabled={isSubmitting}
                autoComplete="family-name"
                required
              />
            </div>
          </div>

          <div style={styles.fieldWrap}>
            <label htmlFor="su-email" style={styles.label}>Email</label>
            <input
              id="su-email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('email')}
              disabled={isSubmitting}
              autoComplete="email"
              required
            />
          </div>

          <div style={styles.fieldWrap}>
            <label htmlFor="su-password" style={styles.label}>Password</label>
            <input
              id="su-password"
              name="password"
              type="password"
              placeholder="••••••••"
              onChange={handleChange}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('password')}
              disabled={isSubmitting}
              autoComplete="new-password"
              required
            />
          </div>

          {/* Password strength pills */}
          <div style={styles.checksGrid} role="group" aria-label="Password requirements">
            {CHECK_ITEMS.map(({ key, label }) => (
              <span
                key={key}
                style={{
                  ...styles.checkPill,
                  ...(checks[key] ? styles.checkPillActive : styles.checkPillInactive),
                }}
              >
                <span style={styles.checkIcon} aria-hidden="true">
                  {checks[key] ? '✓' : '○'}
                </span>
                {label}
              </span>
            ))}
          </div>

          <div style={styles.fieldWrap}>
            <label htmlFor="su-confirmPassword" style={styles.label}>Confirm Password</label>
            <input
              id="su-confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="••••••••"
              onChange={handleChange}
              onFocus={() => setFocusedField('confirmPassword')}
              onBlur={() => setFocusedField(null)}
              style={{
                ...inputStyle('confirmPassword'),
                ...(formData.confirmPassword
                  ? checks.match
                    ? { borderColor: 'var(--color-success)', boxShadow: 'var(--glow-success)' }
                    : { borderColor: 'var(--color-error)',   boxShadow: 'var(--glow-error)'   }
                  : {}),
              }}
              disabled={isSubmitting}
              autoComplete="new-password"
              required
            />
          </div>

          <button
            type="submit"
            style={{ ...styles.submitBtn, opacity: isSubmitting ? 0.65 : 1 }}
            disabled={isSubmitting}
            className="hover-lift"
          >
            {isSubmitting ? (
              <span style={styles.btnInner}>
                <span style={styles.btnSpinner} />
                Creating account…
              </span>
            ) : 'Create Account'}
          </button>
        </form>

        {/* Status */}
        {status && (
          <div
            role="status"
            aria-live="polite"
            style={{
              ...styles.statusMsg,
              color: statusColors[status.type]?.color,
              backgroundColor: statusColors[status.type]?.bg,
              borderColor: statusColors[status.type]?.border,
            }}
          >
            {status.text}
          </div>
        )}

        <p style={styles.footerLink}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>Sign in</Link>
        </p>
      </main>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 20px',
    background: 'linear-gradient(145deg, #f0ede7 0%, #e8eee5 55%, #e7ecf0 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  blobTopRight: {
    position: 'absolute', top: '-120px', right: '-140px',
    width: '420px', height: '420px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(22,163,74,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blobBottomLeft: {
    position: 'absolute', bottom: '-100px', left: '-120px',
    width: '360px', height: '360px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(35,43,34,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative', zIndex: 1,
    width: '100%', maxWidth: '460px',
    borderRadius: 'var(--radius-2xl)',
    padding: '38px 40px 34px',
    animation: 'rz-fadeIn 0.4s ease-out both',
  },
  brandArea: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: '10px', marginBottom: '6px',
  },
  logoMark: { fontSize: '24px', color: 'var(--color-charcoal-800)', lineHeight: 1 },
  brandName: {
    fontFamily: 'var(--font-heading)', fontSize: '28px',
    fontWeight: 700, letterSpacing: '-0.5px',
    color: 'var(--color-charcoal-900)', margin: 0,
  },
  tagline: {
    textAlign: 'center', color: 'var(--color-text-secondary)',
    fontSize: 'var(--text-sm)', marginBottom: '26px',
    fontWeight: 'var(--font-weight-medium)',
  },
  googleBtn: {
    width: '100%', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: '10px',
    padding: '13px 20px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    transition: 'all var(--transition-base)',
    boxShadow: 'var(--shadow-xs)',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: '12px', margin: '22px 0',
  },
  dividerLine: { flex: 1, height: '1px', backgroundColor: 'var(--color-border)' },
  dividerText: {
    fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
    fontWeight: 'var(--font-weight-medium)', letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  nameRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: '7px' },
  label: {
    fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  input: {
    padding: '12px 14px',
    border: '1.5px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-surface)',
    outline: 'none',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
    width: '100%',
  },
  inputFocused: {
    borderColor: 'var(--color-charcoal-700)',
    boxShadow: 'var(--glow-accent)',
  },
  checksGrid: {
    display: 'flex', flexWrap: 'wrap', gap: '8px',
    padding: '12px', borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-surface-alt)',
    border: '1px solid var(--color-border-subtle)',
  },
  checkPill: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '4px 10px', borderRadius: 'var(--radius-full)',
    fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)',
    border: '1px solid',
    transition: 'all var(--transition-base)',
  },
  checkPillActive: {
    color: 'var(--color-emerald-700)',
    backgroundColor: 'var(--color-emerald-050)',
    borderColor: 'var(--color-emerald-100)',
    animation: 'rz-checkPop 0.25s ease-out',
  },
  checkPillInactive: {
    color: 'var(--color-text-muted)',
    backgroundColor: 'var(--color-surface)',
    borderColor: 'var(--color-border)',
  },
  checkIcon: { fontSize: '10px' },
  submitBtn: {
    width: '100%', padding: '14px 20px',
    background: '#ffffff',
    color: '#000000',
    border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-weight-bold)',
    fontSize: 'var(--text-base)', letterSpacing: '0.01em', marginTop: '4px',
    transition: 'opacity var(--transition-base), transform var(--transition-base), box-shadow var(--transition-base)',
    boxShadow: '0 4px 14px rgba(13,15,13,0.18)',
  },
  btnInner: { display: 'inline-flex', alignItems: 'center', gap: '10px' },
  btnSpinner: {
    display: 'inline-block', width: '16px', height: '16px',
    borderRadius: '50%', border: '2px solid rgba(0,0,0,0.1)',
    borderTopColor: '#000', animation: 'rz-spin 0.8s linear infinite',
  },
  statusMsg: {
    marginTop: '16px', padding: '12px 14px',
    borderRadius: 'var(--radius-md)', border: '1px solid',
    fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)',
    textAlign: 'center', animation: 'rz-fadeInFlat 0.2s ease-out',
  },
  footerLink: {
    textAlign: 'center', marginTop: '22px',
    fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)',
  },
  link: {
    color: 'var(--color-charcoal-800)',
    fontWeight: 'var(--font-weight-semibold)',
    borderBottom: '1px solid var(--color-border-strong)',
    transition: 'border-color var(--transition-fast)',
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginTop: '20px',
    animation: 'rz-fadeInFlat 0.3s ease-out both',
  },
  badgeWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  glowCircle: {
    position: 'absolute',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(79, 70, 229, 0.2) 0%, transparent 70%)',
    animation: 'pulse-glow 2s infinite ease-in-out',
  },
  mailIconCelebrating: {
    fontSize: '56px',
    zIndex: 2,
    animation: 'float-icon 3s ease-in-out infinite',
  },
  successHeading: {
    fontFamily: 'var(--font-heading)',
    fontSize: '24px',
    fontWeight: 700,
    color: 'var(--color-charcoal-900)',
    margin: '0 0 8px 0',
  },
  celebrationText: {
    fontSize: '15px',
    fontWeight: 'var(--font-weight-medium)',
    color: '#4f46e5',
    margin: '0 0 20px 0',
    lineHeight: '1.5',
  },
  emailNotificationCard: {
    width: '100%',
    padding: '16px 20px',
    borderRadius: 'var(--radius-lg)',
    background: 'rgba(79, 70, 229, 0.05)',
    border: '1px solid rgba(79, 70, 229, 0.15)',
    marginBottom: '20px',
  },
  successText: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.6',
    margin: 0,
  },
  subtext: {
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    lineHeight: '1.5',
    margin: '0 0 24px 0',
  },
  premiumRedirectBtn: {
    width: '100%',
    padding: '14px 20px',
    background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-heading)',
    fontWeight: 'var(--font-weight-bold)',
    fontSize: 'var(--text-base)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(79, 70, 229, 0.3)',
  },
};

export default Signup;